import { useState } from 'react';
import type { UniqueIdentifier } from '@dnd-kit/core';
import type { ProposalProductItemType } from '@econtractor/types';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import { buildSalePatch } from '../../model/templatePriceUtils';
import { getSortableRowId, reorderItemsById } from '../../model/sortableTableRows';

export interface UseProductsTableParams {
    products: ProposalProductItemType[];
    rate: number;
    onChange: (products: ProposalProductItemType[]) => void;
}

const productRowId = (row: ProposalProductItemType, index: number) =>
    getSortableRowId(row, `${row.productId}-${index}`);

const useProductsTable = ({ products, rate, onChange }: UseProductsTableParams) => {
    const [editNameIndex, setEditNameIndex] = useState<number | null>(null);
    const [editPriceIndex, setEditPriceIndex] = useState<number | null>(null);
    const [editCountIndex, setEditCountIndex] = useState<number | null>(null);

    const removeRow = (targetIndex: number) => {
        onChange(products.filter((_, index) => index !== targetIndex));
    };

    const updateRow = (targetIndex: number, patch: Partial<ProposalProductItemType>) => {
        onChange(products.map((row, index) => (index === targetIndex ? { ...row, ...patch } : row)));
    };

    const reorderByIds = (activeId: UniqueIdentifier, overId: UniqueIdentifier) => {
        onChange(reorderItemsById(products, activeId, overId, productRowId));
    };

    const clearInlineEdits = () => {
        setEditNameIndex(null);
        setEditPriceIndex(null);
        setEditCountIndex(null);
    };

    const openNameEdit = (index: number) => {
        setEditPriceIndex(null);
        setEditCountIndex(null);
        setEditNameIndex(index);
    };

    const openPriceEdit = (index: number) => {
        setEditNameIndex(null);
        setEditCountIndex(null);
        setEditPriceIndex(index);
    };

    const savePriceEdit = (index: number, value: string | number) => {
        updateRow(index, buildSalePatch(safeFloatParse(value || 0) || 0, rate));
        setEditPriceIndex(null);
    };

    const openCountEdit = (index: number) => {
        setEditNameIndex(null);
        setEditPriceIndex(null);
        setEditCountIndex(index);
    };

    const saveCountEdit = (index: number, value: string | number) => {
        const nextCount = safeFloatParse(value || 0) || 0;
        if (nextCount <= 0) return false;
        updateRow(index, { itemCount: nextCount });
        setEditCountIndex(null);
    };

    return {
        editNameIndex,
        setEditNameIndex,
        editPriceIndex,
        setEditPriceIndex,
        editCountIndex,
        setEditCountIndex,
        openNameEdit,
        openPriceEdit,
        savePriceEdit,
        openCountEdit,
        saveCountEdit,
        updateRow,
        removeRow,
        reorderByIds,
        clearInlineEdits,
        getRowId: productRowId,
    };
};

export default useProductsTable;
